import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import EditIcon from '@material-ui/icons/Edit';
import MeetingRoomOutlinedIcon from '@material-ui/icons/MeetingRoomOutlined';
import PrintIcon from '@material-ui/icons/Print';
import { DataGrid } from '@material-ui/data-grid';
import MUIDataTable from 'mui-datatables';

import { formatActivityTime } from '../util/dateHelpers';
import { GET_CARDHOLDER, REQUEST_MOBILE_CREDENTIAL } from '../util/schema';
import FileCopyIcon from '../icons/FileCopyIcon.jsx';
import SendToMobileIcon from '../icons/SendToMobileIcon.jsx';
import CardAddEdit from './CardAddEdit.jsx';
import CardCopy from './CardCopy.jsx';
import CardDelete from './CardDelete.jsx';
import CardPrint from './CardPrint.jsx';
import CredentialAdd from './CredentialAdd.jsx';
import CredentialEdit from './CredentialEdit.jsx';
import CredentialResend from './CredentialResend.jsx';
import TableFooterWithFirstLastButtons from './TableFooterWithFirstLastButtons.jsx';

const useStyles = makeStyles((theme) => ({
  dataTable: {
    boxShadow: 'none',
    '& tbody td:last-child': {
      width: '23.5%',
    },
    '& td, th': {
      padding: '7px',
      cursor: 'pointer',
    },
  },
  content: {
    padding: theme.spacing(1),
    paddingTop: theme.spacing(2),
    lineHeight: '70px',
  },
  newcardcredential: {
    padding: theme.spacing(1),
    paddingTop: theme.spacing(2),
    lineHeight: '70px',
    textAlign: 'center',
  },
  blueMenu: {
    backgroundColor: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  menuButton: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    color: theme.palette.secondary.contrastText,
  },
  activeButton: {
    borderBottom: `1px solid ${theme.palette.secondary.contrastText}`,
  },
  dataTableIcon: {
    marginRight: theme.spacing(1),
  },
  cardCopyIcon: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(-1),
    marginRight: theme.spacing(2.25),
  },
  green: {
    color: '#76e2ac',
  },
  gray: {
    color: theme.palette.neutral.dark,
  },
  addIcon: {
    color: '#3c3b54',
  },
  lastSeenCell: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  dataTableActions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
  },
  tableName: {
    marginLeft: theme.spacing(1),
    color: theme.palette.neutral.dark,
  },
  dataTableIconButton: {
    padding: theme.spacing(1),
  },
  printBtnHide: {
    opacity: 0,
    visibility: 'hidden',
    left: '55px',
    transition: 'visibility 0.5s, left 0.5s, opacity 0.5s linear',
  },
  printBtnShow: {
    opacity: 1,
    visibility: 'visible',
    left: '0px',
    transition: 'visibility 0.5s, left 0.5s, opacity 0.5s linear',
  },
}));

const GET_PRINTERS = gql`
  query printQueues(
    $first: Int
    $skip: Int
    $searchField: String
    $isActive: Boolean
    $isMonitored: Boolean
  ) {
    printQueues(
      first: $first
      skip: $skip
      searchField: $searchField
      isActive: $isActive
      isMonitored: $isMonitored
    ) {
      edges {
        printQueueId
        printerName
      }
    }
  }
`;

const CardsDevices = ({
  cardholder,
  updateCard,
  addCard,
  accessGroups,
  readers,
  changePrimaryCard,
  deleteCards,
  deleteCredential,
  resendCredential,
  isMobile,
  cardholderId,
  showPopper,
  mobileCredentialSuccess,
  mobileCredentialFailure,
  updateMobileCredential,
}) => {
  const [selectedCard, setSelectedCard] = useState({});
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [selectedCredential, setSelectedCredential] = useState({});
  const [selectedCredentialIndex, setSelectedCredentialIndex] = useState(null);
  const [showCardAddEdit, setShowCardAddEdit] = useState(false);
  const [showCardDelete, setShowCardDelete] = useState(false);
  const [showCardPrint, setshowCardPrint] = useState(false);
  const [showCardCopy, setshowCardCopy] = useState(false);
  const [showCredentialAdd, setShowCredentialAdd] = useState(false);
  const [showCredentialResend, setShowCredentialResend] = useState(false);
  const [showCredentialEdit, setShowCredentialEdit] = useState(false);
  const [showCredentialDelete, setShowCredentialDelete] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [hoveredRow, setHoveredRow] = useState();
  const [printers, setPrinters] = useState([]);
  const [selectedCardRows, setSelectedCardRows] = useState([]);
  const [selectedCredentialRows, setSelectedCredentialRows] = useState([]);
  const [rowclick, setRowClick] = useState(false);
  const [copyDetail, setCopyDetail] = useState(null);
  const [fieldsShown, setFieldsShown] = useState('credentials');
  const classes = useStyles();

  const handleCardSelected = (currentRowSelected, allRowsSelected, rowsSelected) => {
    if (allRowsSelected.length === 0) {
      setSelectedCardRows([]);
    }
    const updatedSelectedCardRows = allRowsSelected.map((selectedRow) => selectedRow.dataIndex);
    if (allRowsSelected.length === 1) {
      setSelectedCard(cardholder.cards[updatedSelectedCardRows[0]]);
      setSelectedCardIndex(updatedSelectedCardRows[0]);
      setRowClick(true);
    } else {
      setRowClick(false);
    }

    setSelectedCardRows(updatedSelectedCardRows);
  };

  const handleCredentialSelected = (_currentRowSelected, allRowsSelected) => {
    if (allRowsSelected.length === 0) {
      setSelectedCredentialRows([]);
    }
    const updatedSelectedCredentialRows = allRowsSelected.map(
      (selectedRow) => selectedRow.dataIndex
    );
    if (allRowsSelected.length === 1) {
      setSelectedCredential(cardholder.mobileCredentials[updatedSelectedCredentialRows[0]]);
      setSelectedCredentialIndex(updatedSelectedCredentialRows[0]);
      setRowClick(true);
    } else {
      setRowClick(false);
    }

    setSelectedCredentialRows(updatedSelectedCredentialRows);
  };

  const handleActiveCheckbox = (event, cardIndex) => {
    const mutableCards = cardholder.cards.map((card) => card);
    const updatedCard = { ...mutableCards[cardIndex] };
    updatedCard.active = event.target.checked;
    updateCard(updatedCard, cardIndex);
  };

  const handleActiveCredentialCheckbox = (event, credentialIndex) => {
    const mutableCredentials = cardholder.mobileCredentials.map((credential) => credential);
    const updatedCredential = { ...mutableCredentials[credentialIndex] };
    updatedCredential.active = event.target.checked;
    updateMobileCredential(updatedCredential, credentialIndex);
  };

  const handleChangePrimary = (event, cardIndex) => {
    changePrimaryCard(cardIndex);
  };

  const queryPrinterNamesCompleted = (response) => {
    setPrinters(response.printQueues.edges);
  };

  const { loading: loadingSingleReader, ERROR: errorSingleReader } = useQuery(GET_PRINTERS, {
    variables: { isActive: true, isMonitored: true },
    notifyOnNetworkStatusChange: true,
    onCompleted: queryPrinterNamesCompleted,
  });

  const [requestMobileCredential] = useMutation(REQUEST_MOBILE_CREDENTIAL, {
    notifyOnNetworkStatusChange: true,
    onCompleted: mobileCredentialSuccess,
    onError: mobileCredentialFailure,
    // eslint-disable-next-line no-shadow
    update(cache, { data: { requestMobileCredential } }) {
      const existing = cache.readQuery({
        query: GET_CARDHOLDER,
        variables: { cardholderId: cardholder.id },
      });

      const newCardholder = { ...existing.cardholder };

      if (newCardholder.mobileCredentials) {
        newCardholder.mobileCredentials = [
          ...newCardholder.mobileCredentials,
          requestMobileCredential,
        ];
      } else {
        newCardholder.mobileCredentials = [requestMobileCredential];
      }

      cache.writeQuery({
        query: GET_CARDHOLDER,
        variables: { cardholderId: cardholder.id },
        data: { cardholder: { ...newCardholder } },
      });
    },
  });

  const loadPrinters = () => {
    setshowCardPrint(true);
  };

  const formatCardData = (cards) => {
    return cards.map((card, index) => {
      const activeCheckbox = (
        <Checkbox
          checked={card.active || false}
          onChange={(event) => {
            handleActiveCheckbox(event, index);
          }}
        />
      );
      const primaryCheckbox = (
        <Checkbox
          checked={card.primary || false}
          onChange={(event) => {
            handleChangePrimary(event, index);
          }}
        />
      );
      const lastSeen = card.lastActivity ? (
        <div className={classes.lastSeenCell}>
          <MeetingRoomOutlinedIcon className={`${classes.dataTableIcon} ${classes.green}`} />
          {formatActivityTime(card.lastActivity)}
        </div>
      ) : (
        <div className={classes.lastSeenCell}>
          <MeetingRoomOutlinedIcon className={`${classes.dataTableIcon} ${classes.grey}`} />
          No Activity
        </div>
      );
      const isHoveredRow = index === hoveredRow;
      const actions = (
        <div className={classes.dataTableActions}>
          <IconButton
            onClick={() => {
              setSelectedCard(card);
              loadPrinters();
            }}
            disableRipple
            className={
              isHoveredRow
                ? `${classes.dataTableIconButton} ${classes.printBtnShow}`
                : `${classes.dataTableIconButton} ${classes.printBtnHide}`
            }
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            className={classes.dataTableIconButton}
            disableRipple
            disabled={!isHoveredRow}
            onClick={() => {
              setCopyDetail(null);
              setSelectedCard(card);
              setSelectedCardIndex(index);
              setShowCardAddEdit(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            className={classes.dataTableIconButton}
            disableRipple
            disabled={!isHoveredRow}
            onClick={() => {
              setSelectedCard(card);
              setSelectedCardIndex(index);
              setShowCardDelete(true);
            }}
          >
            <CancelIcon />
          </IconButton>
        </div>
      );

      return [card.cardNumber, activeCheckbox, primaryCheckbox, lastSeen, actions];
    });
  };

  const formatMobileCredentialData = (mobileCredentials) => {
    return mobileCredentials.map((credential, index) => {
      const activeCheckbox = (
        <Checkbox
          checked={credential.active || false}
          onChange={(event) => {
            handleActiveCredentialCheckbox(event, index);
          }}
        />
      );
      const isHoveredRow = index === hoveredRow;
      const actions = (
        <div className={classes.dataTableActions}>
          <IconButton
            className={classes.dataTableIconButton}
            disableRipple
            disabled={!isHoveredRow}
            onClick={() => {
              setSelectedCredential(credential);
              setSelectedCredentialIndex(index);
              setShowCredentialResend(true);
            }}
          >
            <SendToMobileIcon />
          </IconButton>
          <IconButton
            className={classes.dataTableIconButton}
            disableRipple
            disabled={!isHoveredRow}
            onClick={() => {
              setSelectedCredential(credential);
              setSelectedCredentialIndex(index);
              setShowCredentialEdit(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            className={classes.dataTableIconButton}
            disableRipple
            disabled={!isHoveredRow}
            onClick={() => {
              setSelectedCredential(credential);
              setSelectedCredentialIndex(index);
              setShowCredentialDelete(true);
            }}
          >
            <CancelIcon />
          </IconButton>
        </div>
      );

      return [credential.credentialNumber, activeCheckbox, actions];
    });
  };

  const addDevice = () => {
    setSelectedCard({});
    setSelectedCardIndex(null);
    setShowCardAddEdit(true);
  };

  const addCredential = () => {
    setSelectedCredential({});
    setSelectedCredentialIndex(null);
    setShowCredentialAdd(true);
  };

  const deleteMultipleCards = () => {
    setShowCardDelete(true);
  };

  const cardCopy = () => {
    setSelectedCard(cardholder.cards[selectedCardIndex]);
    setshowCardCopy(true);
  };

  const actionColumn = {
    name: 'actions',
    options: {
      filteR: false,
      sort: false,
      // eslint-disable-next-line react/display-name
      customHeadLabelRender: () => {
        return (
          <div className={classes.dataTableActions}>
            <div className={classes.cardCopyIcon}>
              {rowclick ? <FileCopyIcon style={{ color: '#3c3b54' }} onClick={cardCopy} /> : null}
            </div>
            {selectedCardRows.length < 2 && (
              <AddCircleIcon
                className={`${classes.addIcon} ${classes.dataTableIcon}`}
                onClick={addDevice}
              />
            )}
            {selectedCardRows.length > 1 && (
              <CancelIcon className={classes.dataTableIcon} onClick={deleteMultipleCards} />
            )}
          </div>
        );
      },
    },
  };

  const credentialActionColumn = {
    name: 'actions',
    options: {
      filteR: false,
      sort: false,
      // eslint-disable-next-line react/display-name
      customHeadLabelRender: () => {
        return (
          <div className={classes.dataTableActions}>
            {selectedCredentialRows.length < 2 && (
              <AddCircleIcon
                className={`${classes.addIcon} ${classes.dataTableIcon}`}
                onClick={addCredential}
              />
            )}
          </div>
        );
      },
    },
  };

  const columns = ['Card Number', 'Active', 'Primary', 'Last Active', actionColumn];
  const credentialColumns = ['Credential Number', 'Active', credentialActionColumn]; // TODO: Add 'Last Active' column?

  const saveCard = (cardData) => {
    if (cardData.actionType === 'edit') {
      updateCard(cardData, selectedCardIndex);
    } else {
      addCard(cardData);
    }
  };

  const saveMobileCredential = (credentialData) => {
    updateMobileCredential(credentialData, selectedCredentialIndex);
  };

  const removeCards = (cardsToDelete) => {
    deleteCards(cardsToDelete);
    setSelectedCardIndex(null);
    setSelectedCardRows([]);
  };

  const removeCredential = (credentialToDelete) => {
    deleteCredential(credentialToDelete);
    setSelectedCredentialIndex(null);
    setSelectedCredentialRows([]);
  };

  const resendMobileCredential = (credentialToResend) => {
    resendCredential(credentialToResend);
    setSelectedCredentialIndex(null);
    setSelectedCredentialRows([]);
  };

  const hideCardAddEdit = () => {
    handleCardSelected(null, [], null);
    setShowCardAddEdit(false);
    setSelectedCard({});
    setSelectedCardIndex(null);
  };

  const hideCredentialAdd = () => {
    setShowCredentialAdd(false);
    setSelectedCredential({});
    setSelectedCredentialIndex(null);
  };

  const hideCredentialResend = () => {
    setShowCredentialResend(false);
    setSelectedCredential({});
    setSelectedCredentialIndex(null);
  };

  const hideCredentialEdit = () => {
    setShowCredentialEdit(false);
    setSelectedCredential({});
    setSelectedCredentialIndex(null);
  };

  const hideCardDelete = () => {
    setShowCardDelete(false);
    setSelectedCard({});
    setSelectedCardIndex(null);
  };

  const hideCredentialDelete = () => {
    setShowCredentialDelete(false);
    setSelectedCredential({});
    setSelectedCredentialIndex(null);
  };

  const hideCardPrint = () => {
    setshowCardPrint(false);
  };

  const hideCardCopy = () => {
    handleCardSelected(null, [], null);
    setSelectedCard({});
    setSelectedCardIndex(null);
    setshowCardCopy(false);
  };

  const handleCopy = (detail) => {
    setShowCardAddEdit(true);
    setCopyDetail(detail);
    setshowCardCopy(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditCard = (data) => {
    setSelectedCardIndex(data.row.id);
    showPopper(data.row.cardData, data.row.id);
  };

  const tableOptions = {
    filter: false,
    print: false,
    search: false,
    download: false,
    viewColumns: false,
    rowsPerPage: 5,
    rowsPerPageOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    showFirstButton: true,
    showLastButton: true,
    onRowSelectionChange: handleCardSelected,
    rowsSelected: selectedCardRows,
    selectableRowsOnClick: true,
    customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
      return (
        <TableFooterWithFirstLastButtons
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          changeRowsPerPage={changeRowsPerPage}
          changePage={changePage}
          textLabels={textLabels}
        />
      );
    },
    setRowProps: (row, dataIndex, rowIndex) => {
      return {
        onMouseOver: () => {
          setHoveredRow(dataIndex);
        },
        onMouseLeave: () => {
          setHoveredRow();
        },
      };
    },
  };

  const mobileCredentialTableOptions = {
    filter: false,
    print: false,
    search: false,
    download: false,
    viewColumns: false,
    rowsPerPage: 5,
    rowsPerPageOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    showFirstButton: true,
    showLastButton: true,
    onRowSelectionChange: handleCredentialSelected,
    rowsSelected: selectedCredentialRows,
    selectableRows: 'single',
    selectableRowsOnClick: true,
    customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
      return (
        <TableFooterWithFirstLastButtons
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          changeRowsPerPage={changeRowsPerPage}
          changePage={changePage}
          textLabels={textLabels}
        />
      );
    },
    setRowProps: (row, dataIndex, rowIndex) => {
      return {
        onMouseOver: () => {
          setHoveredRow(dataIndex);
        },
        onMouseLeave: () => {
          setHoveredRow();
        },
      };
    },
  };

  const mobileData = (cards) => {
    return cards.map((card) => {
      const lastSeen = card.lastActivity ? (
        <div className={classes.lastSeenCell}>
          <MeetingRoomOutlinedIcon className={`${classes.dataTableIcon} ${classes.green}`} />
          {formatActivityTime(card.lastActivity)}
        </div>
      ) : (
        <div className={classes.lastSeenCell}>
          <MeetingRoomOutlinedIcon className={`${classes.dataTableIcon} ${classes.grey}`} />
          No Activity
        </div>
      );

      return [card.cardNumber, lastSeen];
    });
  };

  const rows = (cards) => {
    const mapped = cards.map((card, index) => {
      const lastSeen = card.lastActivity ? formatActivityTime(card.lastActivity) : ' No Activity';

      return {
        id: index,
        CardNumber: card.cardNumber,
        LastSeen: lastSeen,
        cardData: card,
      };
    });
    return mapped;
  };

  const columnsMobile = [
    {
      field: '',
      headerName: 'Icon',
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return <CreditCardIcon />;
      },
    },
    { field: 'CardNumber', headerName: 'Card Number' },
    { field: 'LastSeen', headerName: 'Last Seen' },
  ];

  const mobileDataCardz =
    cardholder && cardholder.cards ? (
      <div style={{ height: 400, width: '100%' }}>
        <DataGrid
          autoHeight="true"
          rows={rows(cardholder.cards)}
          onRowClick={handleEditCard}
          columns={[
            {
              field: '',
              headerName: '',
              width: '55px',
              sortable: false,
              disableClickEventBubbling: false,
              renderCell: (params) => {
                return <CreditCardIcon onRowClick={handleEditCard} />;
              },
            },
            { field: 'CardNumber', headerName: 'Card #', width: '95px' },
            { field: 'LastSeen', headerName: 'Last Seen', width: '114px' },
          ]}
        />
        {/* <MUIDataTable title={'Cards'} data={mobileDataCards} columns={columnsMobile}></MUIDataTable> */}
      </div>
    ) : (
      <>
        <div className={classes.newcardcredential}>
          <Typography key="message">No Credentials Available</Typography>
          <Button
            className={classes.button}
            variant="contained"
            onClick={addDevice}
            key="messageButton"
            color="secondary"
          >
            Add a Credential
          </Button>
        </div>
      </>
    );

  const renderedCards =
    cardholder && cardholder.cards ? (
      <MUIDataTable
        data={formatCardData(cardholder.cards)}
        columns={columns}
        options={tableOptions}
        className={classes.dataTable}
      />
    ) : (
      <>
        <div className={classes.newcardcredential}>
          <Typography key="message">No Credentials Available</Typography>
          <Button
            className={classes.button}
            variant="contained"
            onClick={addDevice}
            key="messageButton"
            color="secondary"
          >
            Add a Credential
          </Button>
        </div>
      </>
    );

  const renderedMobileCredentials =
    cardholder && cardholder.mobileCredentials ? (
      <MUIDataTable
        data={formatMobileCredentialData(cardholder.mobileCredentials)}
        columns={credentialColumns}
        options={mobileCredentialTableOptions}
        className={classes.dataTable}
      />
    ) : (
      <>
        <div className={classes.newcardcredential}>
          <Typography key="message">No Mobile Credentials Available</Typography>
          <Button
            className={classes.button}
            variant="contained"
            onClick={() => setShowCredentialAdd(true)}
            key="messageButton"
            color="secondary"
          >
            Add a Mobile Credential
          </Button>
        </div>
      </>
    );

  return (
    <>
      <div className={classes.blueMenu}>
        <Button
          className={`${classes.menuButton} ${
            fieldsShown === 'credentials' ? classes.activeButton : ''
          }`}
          onClick={() => setFieldsShown('credentials')}
        >
          {isMobile ? 'Cards & Devices' : 'Credentials'}
        </Button>
        <Button
          className={`${classes.menuButton} ${
            fieldsShown === 'mobileCredentials' ? classes.activeButton : ''
          }`}
          onClick={() => setFieldsShown('mobileCredentials')}
        >
          Mobile Credentials
        </Button>
        {/* <Button className={classes.menuButton}>Activity Report</Button> */}
      </div>

      {fieldsShown === 'credentials' && (
        <div className={classes.content}>{!isMobile ? renderedCards : mobileDataCardz}</div>
      )}

      {fieldsShown === 'mobileCredentials' && (
        <div className={classes.content}>{renderedMobileCredentials}</div> // TODO: mobile rendering?
      )}

      <CardAddEdit
        visible={showCardAddEdit}
        card={selectedCard}
        selectedCardIndex={selectedCardIndex} // TODO: Remove?
        dismiss={hideCardAddEdit}
        onSubmit={saveCard}
        allAccessGroups={accessGroups}
        allSingleReaderAccessRights={readers}
        isFirstCard={cardholder.cards && cardholder.cards.length === 0}
        copyDetail={copyDetail}
      />
      <CredentialAdd
        visible={showCredentialAdd}
        dismiss={hideCredentialAdd}
        requestCredential={requestMobileCredential}
        cardholderId={cardholder.id}
      />
      <CredentialResend
        visible={showCredentialResend}
        dismiss={hideCredentialResend}
        confirm={() => {
          resendMobileCredential(selectedCredentialIndex);
        }}
      />
      <CredentialEdit
        visible={showCredentialEdit}
        credential={selectedCredential}
        dismiss={hideCredentialEdit}
        onSubmit={saveMobileCredential}
        allAccessGroups={accessGroups}
        allSingleReaderAccessRights={readers}
      />
      <CardDelete
        visible={showCardDelete}
        card={selectedCard}
        selectedCardIndex={selectedCardIndex}
        dismiss={hideCardDelete}
        confirm={() => {
          let cardsToDelete = selectedCardRows;
          if (selectedCardIndex || selectedCardIndex === 0) {
            cardsToDelete = [selectedCardIndex];
          }
          removeCards(cardsToDelete);
        }}
      />
      <CardDelete
        visible={showCredentialDelete}
        dismiss={hideCredentialDelete}
        confirm={() => {
          removeCredential(selectedCredentialIndex);
        }}
      />
      <CardPrint
        printers={printers}
        visible={showCardPrint}
        card={selectedCard}
        cardholderId={cardholderId}
        dismiss={hideCardPrint}
      />
      <CardCopy
        visible={showCardCopy}
        card={selectedCard}
        dismiss={hideCardCopy}
        copy={handleCopy}
      />
    </>
  );
};

// TODO: implement proptype validation across the project
// CardsDevices.propTypes = {
//   cardholder: PropTypes.objectOf(PropTypes.object).isRequired,
// };

export default CardsDevices;
